import { getAdjustedCityName, boroughs, NYCCountyNames } from './locationUtils.js';

// Assumes Google Places is loaded
/* global google */

/**
     * Map from prediction descriptions to formatted county names
     */
var formattedBoroughs = {
    'Manhattan, New York, NY, United States': 'New York (Manhattan)',
    'Brooklyn, NY, United States': 'Kings (Brooklyn)',
    'Queens, NY, United States': 'Queens',
    'Bronx, NY, United States': 'Bronx',
    'Staten Island, NY, United States': 'Richmond (Staten Island)',
};

/**
     * Sub-city regions we want to be able to typeahead search
     * It's currently only NYC boroughs, but we'll probably add to it
     */
export const neighborhoods = [
    {
        name: 'Manhattan, NY',
        place_id: 'ChIJOwg_06VPwokRYv534QaPC8g',
    },
    {
        name: 'Brooklyn, NY',
        place_id: 'ChIJOwE7_GTtwokRs75rhW4_I6M',
    },
    {
        name: 'Queens, NY',
        place_id: 'ChIJgav5pFbxwokRno6Tc5x2GL8',
    },
    {
        name: 'Staten Island, NY',
        place_id: 'ChIJOwE7_GTtwokR1V_vES61lRI',
    },
    {
        name: 'Bronx, NY',
        place_id: 'ChIJBUEf6ovgwokRwlazSIxIpsk',
    },
];

var isDC = function (place) {
    return place.formatted_address === 'Washington, DC, USA';
};

export const isState = function (place) {
    return isDC(place) ||
                place.types.some(function (type) {
                    return type === 'administrative_area_level_1';
                });
};

export const getStateCode = function (place) {
    if (isDC(place)) {
        return 'DC';
    }
    return place.address_components.filter(isState)[0].short_name.toUpperCase();
};

export const isCity = function (place) {
    return !isDC(place) &&
            place.types.some(function (type) {
                return type === 'locality' || type === 'administrative_area_level_3' ||
                        (type === 'neighborhood' && place.formatted_address && place.formatted_address.indexOf(', Boston, MA') > -1);
            });
};

/**
 * If there's no city/locality information about a given location, Maps may show a "plus code" result (basically a
 * human-readable lat/long) before showing mailing codes, states, counties, etc.)
 * @param place
 */
const isPlusCode = (place) => {
    return place.types.length === 1 && place.types[0] === 'plus_code';
}
const isNotPlusCode = (place) => {
    return !isPlusCode(place);
}

export const getCityName = function (place) {
    var stateCode = getStateCode(place);

    if (isCity(place) && place.name) {
        return getAdjustedCityName(place.name, stateCode);
    } else {
        var cityInfo = place.address_components.filter(isCity)[0];
        if (cityInfo) {
            return getAdjustedCityName(cityInfo.long_name, stateCode);
        }
    }
};

export const isCounty = function (place) {
    if (isDC(place)) {
        return false;
    }
    var normalCounty = place.types.some(function (type) {
        return type === 'administrative_area_level_2';
    });
    var borough = getStateCode(place) === 'NY' &&
                        boroughs[place.name];
    return normalCounty || borough;
};

var countySuffixPattern = / (County|Parish)$/;

export const getCountyName = function (place) {
    if (isCounty(place)) {
        var name = place.name.replace(countySuffixPattern, '');
        var stateCode = getStateCode(place);
        if (stateCode === 'NY') {
            if (NYCCountyNames[name]) {
                name = NYCCountyNames[name];
            } else if (boroughs[name]) {
                name = boroughs[name];
            }
        }

        // Even though Louisiana uses parishes, we have the names end in "county" in our search engine
        return name + 'county';
    } else {
        var countyInfo = place.address_components.filter(function (component) {
            return component.types.some(function (type) {
                return type === 'administrative_area_level_2';
            });
        })[0];
        if (countyInfo) {
            return countyInfo.long_name.replace(countySuffixPattern, 'county');
        }
    }
};

export const getZip = function (place) {
    if (place.types[0] === 'postal_code') {
        return place.name;
    }
};

export const getLatLng = function (place) {
    var location = place.geometry.location;
    return {
        lat: parseFloat(location.lat()).toFixed(4),
        lng: parseFloat(location.lng()).toFixed(4),
    };
};

export const isDesiredPrediction = function (prediction) {
    return formattedBoroughs[prediction.description] ||
                prediction.types.some(function (type) {
                    return type === 'locality' ||
                            type === 'administrative_area_level_3' ||
                            type === 'administrative_area_level_2' ||
                            type === 'administrative_area_level_1' ||
                            type === 'postal_code' || (
                        type === 'neighborhood' &&
                                prediction.description.indexOf(', Boston, MA') > -1
                    );
                });
};

/**
     * Uses the Google Places API to look up information about a lat/lng
     * @param {number} lat
     * @param {number} lng
     * @param {function} addressCallback
     * @return {!object} An object representing the location
     */
export const geocode = function (lat, lng, addressCallback) {
    var geocoder = new google.maps.Geocoder();
    var requestLatLng = new google.maps.LatLng(lat, lng);

    geocoder.geocode({ location: requestLatLng }, function (results, status) {
        if (status == google.maps.GeocoderStatus.OK && results.length > 0) {
            // Try to retrieve a city, which is specific enough for good results but will also give us decent cache behavior
            let place = results.filter(isCity)[0];

            // DC is handled as something more like a state than a city. Try that next.
            if (place === undefined) {
                place = results.filter(isDC)[0];
            }

            // Maps will sometimes prioritize a "plus code" response (essentially a more-human-readable lat/long) over
            // vaguer state-level or postal-code-level responses. But if we're calling this function, we probably
            // already have a lat/long and want state/county info instead. So take the first non-plus-code if possible.
            if (place === undefined) {
                place = results.filter(isNotPlusCode)[0];
            }

            // Default to the first item otherwise.
            if (place === undefined) {
                place = results[0];
            }

            addressCallback({
                zip: getZip(place),
                cityName: getCityName(place),
                stateCode: getStateCode(place),
                countyName: getCountyName(place),
                latLng: getLatLng(place),
            });
        }
    });
};

export const geocodeByZip = function (zip, successCallback, failureCallback) {
    var geocoder = new google.maps.Geocoder();
    geocoder.geocode(
        {
            componentRestrictions: {
                country: 'US',
                postalCode: zip,
            },
        },
        geocodeByZipCallback(successCallback, failureCallback),
    );
};

var geocodeByZipCallback = function (successCallback, failureCallback) {
    return function (results, status) {
        if (status === google.maps.GeocoderStatus.OK && results.length > 0) {
            var result = results[0];
            var formattedAddress = result.formatted_address;
            var cityName = formattedAddress.substring(0, formattedAddress.indexOf(','));
            successCallback({
                cityName: cityName,
                latLng: getLatLng(result),
            });
        } else {
            failureCallback();
        }
    };
};
